import { Helmet } from "react-helmet-async";
import { Reveal } from "../components/Reveal";
import Gallery from "../components/Gallery";
import { GALLERY_PHOTOS } from "../lib/photos";

const VIDEOS = [
  "/media/videos/salumi-1.mp4",
  "/media/videos/salumi-2.mp4",
  "/media/videos/salumi-3.mp4",
  "/media/videos/salumi-5.mp4",
  "/media/videos/salumi-7.mp4",
  "/media/videos/salumi-9.mp4",
  "/media/videos/salumi-10.mp4",
  "/media/videos/salumi-11.mp4",
  "/media/videos/salumi-12.mp4",
  "/media/videos/salumi-13.mp4",
  "/media/videos/salumi-14.mp4",
];

/**
 * Build a mixed-media sequence where the 11 videos are sprinkled through
 * the 33 photos at irregular intervals (no two videos adjacent, never
 * lined up in a regular every-Nth pattern).
 *
 * Hand-picked positions index INTO the final merged array; chosen so that
 * across the 4-column masonry, videos feel scattered rather than periodic.
 */
function buildMixedMedia() {
  // Insertion positions (0-indexed) in the merged sequence of 33 photos + 11 videos = 44.
  // Spacing varies between 2–6 items, never adjacent.
  const VIDEO_POSITIONS = [2, 6, 11, 15, 20, 24, 28, 32, 36, 39, 42];

  const photoQueue = [...GALLERY_PHOTOS];
  const videoQueue = [...VIDEOS];
  const merged = [];

  const total = photoQueue.length + videoQueue.length;
  const videoSet = new Set(VIDEO_POSITIONS);

  for (let i = 0; i < total; i++) {
    if (videoSet.has(i) && videoQueue.length) {
      merged.push({ type: "video", src: videoQueue.shift() });
    } else if (photoQueue.length) {
      merged.push({ type: "photo", src: photoQueue.shift() });
    } else if (videoQueue.length) {
      // Spillover (shouldn't happen with current counts).
      merged.push({ type: "video", src: videoQueue.shift() });
    }
  }
  return merged;
}

const GALLERY_MEDIA = buildMixedMedia();

export default function GalleryPage() {
  return (
    <>
      <Helmet>
        <title>Gallery — A Look at the Table | Salumi Tucson</title>
        <meta
          name="description"
          content="A photographic journal of meals, cocktails, fresh pasta, and gatherings at Salumi Tucson — private cooking classes, seasonal workshops, and culinary experiences in Tucson, Arizona."
        />
        <link rel="canonical" href="https://salumitucson.com/gallery" />
      </Helmet>

      <section className="bg-sand text-espresso pt-40 md:pt-56 pb-20 md:pb-28" data-testid="gallery-hero">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.3em] text-olive mb-6">From the kitchen</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl tracking-editorial leading-[0.9]">
              The <em className="not-italic text-terracotta">gallery</em>.
            </h1>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-10 max-w-2xl text-lg text-espresso/75 leading-relaxed">
              A photographic record of the meals we create and culinary experiences we share — pasta freshly cut, drinks poured, desserts plated, friends gathered.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory text-espresso py-20 md:py-28" data-testid="gallery-grid-section">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Gallery media={GALLERY_MEDIA} columns={4} gap="md" />
        </div>
      </section>
    </>
  );
}
